/**
 * 商品サムネイル一覧用のカスタムスクロールバー。
 * macOS のオーバーレイスクロールバーに依存せず、常時表示の進捗バー＋ドラッグ操作を提供する。
 */
class ThumbnailScrollbar {
  constructor(scrollbarEl, listEl) {
    this.scrollbar = scrollbarEl;
    this.track = scrollbarEl.querySelector('.thumbnail-scrollbar__track');
    this.thumb = scrollbarEl.querySelector('.thumbnail-scrollbar__thumb');
    this.list = listEl;
    this.dragging = false;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onThumbPointerDown = this.onThumbPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onTrackPointerDown = this.onTrackPointerDown.bind(this);

    this.init();
  }

  init() {
    this.list.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.list);
    }

    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => this.update());
      this.mutationObserver.observe(this.list, { childList: true, subtree: true });
    }

    this.thumb.addEventListener('pointerdown', this.onThumbPointerDown);
    this.track.addEventListener('pointerdown', this.onTrackPointerDown);

    this.update();
  }

  update() {
    const scrollable = this.list.scrollWidth - this.list.clientWidth > 1;
    this.scrollbar.classList.toggle('is-active', scrollable);
    if (!scrollable) return;

    const ratio = this.list.clientWidth / this.list.scrollWidth;
    this.thumb.style.width = `${Math.max(ratio * 100, 0)}%`;
    this.updateThumbPosition();
  }

  updateThumbPosition() {
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    if (maxScroll <= 0) {
      this.thumb.style.transform = 'translateX(0)';
      return;
    }
    const trackWidth = this.track.clientWidth;
    const thumbWidth = this.thumb.offsetWidth;
    const scrollable = trackWidth - thumbWidth;
    const ratio = this.list.scrollLeft / maxScroll;
    const left = ratio * scrollable;
    this.thumb.style.transform = `translateX(${left}px)`;
  }

  onScroll() {
    this.updateThumbPosition();
  }

  onResize() {
    this.update();
  }

  onThumbPointerDown(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.list.scrollLeft;
    this.scrollbar.classList.add('is-dragging');
    this.thumb.setPointerCapture(event.pointerId);
    this.thumb.addEventListener('pointermove', this.onPointerMove);
    this.thumb.addEventListener('pointerup', this.onPointerUp);
    this.thumb.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const trackWidth = this.track.clientWidth;
    const thumbWidth = this.thumb.offsetWidth;
    const scrollable = trackWidth - thumbWidth;
    if (scrollable <= 0) return;
    const deltaX = event.clientX - this.dragStartX;
    const scrollDelta = (deltaX / scrollable) * maxScroll;
    this.list.scrollLeft = this.dragStartScrollLeft + scrollDelta;
  }

  onPointerUp(event) {
    this.dragging = false;
    this.scrollbar.classList.remove('is-dragging');
    if (this.thumb.hasPointerCapture(event.pointerId)) {
      this.thumb.releasePointerCapture(event.pointerId);
    }
    this.thumb.removeEventListener('pointermove', this.onPointerMove);
    this.thumb.removeEventListener('pointerup', this.onPointerUp);
    this.thumb.removeEventListener('pointercancel', this.onPointerUp);
  }

  onTrackPointerDown(event) {
    if (event.target === this.thumb) return;
    const trackRect = this.track.getBoundingClientRect();
    const clickX = event.clientX - trackRect.left;
    const thumbWidth = this.thumb.offsetWidth;
    const trackWidth = this.track.clientWidth;
    const scrollable = trackWidth - thumbWidth;
    if (scrollable <= 0) return;
    const targetLeft = Math.max(0, Math.min(scrollable, clickX - thumbWidth / 2));
    const ratio = targetLeft / scrollable;
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    this.list.scrollTo({ left: ratio * maxScroll, behavior: 'smooth' });
  }
}

function initThumbnailScrollbars(root = document) {
  const scrollbars = root.querySelectorAll('[data-thumbnail-scrollbar]:not([data-initialized])');
  scrollbars.forEach((scrollbar) => {
    const targetId = scrollbar.getAttribute('data-target');
    const list = targetId ? document.getElementById(targetId) : null;
    if (!list) return;
    scrollbar.setAttribute('data-initialized', 'true');
    new ThumbnailScrollbar(scrollbar, list);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initThumbnailScrollbars());
} else {
  initThumbnailScrollbars();
}

document.addEventListener('shopify:section:load', (event) => {
  initThumbnailScrollbars(event.target);
});
